import React, { useState } from "react";
import AddPaymentModal from "./AddPaymentModal";
import DeletePaymentMethodModal from "./DeletePaymentMethodModal";
import PaymentMethodDetailsModal from "./PaymentMethodDetailsModal";
import { ReactComponent as Delete } from "../../../assets/icons/delete-fill.svg";
import { detectCardBrandKey, getCardBrandMeta } from "../constants/cardBrand";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { useSelector } from "react-redux";
import { isAdmin } from "../../../shared/utils/roles";

const CardIcon = ({ brand }) => {
  const meta = getCardBrandMeta(brand);
  return <img src={meta.icon} alt={meta.label} className="h-6" loading="eager" />;
};

function PaymentCard({ card, onDelete, onSelect, canDelete }) {
  return (
    <div
      className="flex items-center justify-between bg-white border border-line rounded-xl p-4 cursor-pointer hover:shadow-sm transition-shadow"
      onClick={() => onSelect(card)}
    >
      <div className="flex items-center gap-4">
        <CardIcon brand={card.brand} />
        <div>
          <div className="text-sm text-[#171725]">
            •••• •••• •••• {card.last4}{" "}
            {card.default && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">
                Default
              </span>
            )}
          </div>
          <div className="text-xs text-subtle">
            {card.cardStatus === "expired" ? "Expired" : `Expires ${card.exp}`}
          </div>
        </div>
      </div>
      <div>
        <button
          disabled={!canDelete}
          onClick={(event) => {
            event.stopPropagation();
            if (canDelete) {
              onDelete(card);
            }
          }}
          className="disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Delete className="w-5 sm:w-7 h-6 sm:h-8" />
        </button>
      </div>
    </div>
  );
}

export default function PaymentMethods() {
  const currentUser = useSelector(selectCurrentUser);
  const canEditPaymentMethods = isAdmin(currentUser?.user_role);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const {
    cards,
    selectedMethodDetails,
    isLoading,
    isSubmitting,
    isDeleting,
    isLoadingDetails,
    apiError,
    detailsError,
    canEditPaymentMethods: hookCanEditPaymentMethods,
    handleAdd,
    loadDetails,
    handleDelete,
  } = usePaymentMethods();
  const canEdit = canEditPaymentMethods && hookCanEditPaymentMethods;

  const handleDeleteClick = (card) => {
    setDeleteTarget(card);
  };

  const handleCardSelect = (card) => {
    setIsDetailsOpen(true);
    loadDetails(card.id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    const didDelete = await handleDelete(deleteTarget.id);
    if (didDelete) {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl shadow-sm max-sm:px-4 p-6 border border-line sm:mx-2">
      <div className="flex sm:flex-row flex-col items-start sm:items-center justify-between mb-6 sm:gap-0 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Payment Methods</h2>
          <p className="text-sm text-mutedLight">Manage your payment methods</p>
        </div>
        <div>
          <button
            disabled={!canEdit}
            onClick={() => setModalOpen(true)}
            className="bg-slate-800 text-sm sm:text-base text-white px-4 py-1.5 sm:py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Payment Method
          </button>
        </div>
      </div>

      {apiError && <p className="mb-4 text-sm text-red-600">Error: {apiError}</p>}

      {isLoading ? (
        <p className="text-sm text-mutedLight">Loading payment methods...</p>
      ) : (
        <div className="space-y-4">
          {cards.length === 0 ? (
            <p className="text-sm text-mutedLight">No payment methods available, please add first.</p>
          ) : (
            cards.map((card) => (
              <PaymentCard
                key={card.id}
                card={card}
                onDelete={handleDeleteClick}
                onSelect={handleCardSelect}
                canDelete={canEdit}
              />
            ))
          )}
        </div>
      )}

      {canEdit ? (
        <>
          <AddPaymentModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onAdd={handleAdd}
            isSubmitting={isSubmitting}
            errorMessage={apiError}
          />

          <DeletePaymentMethodModal
            open={Boolean(deleteTarget)}
            paymentMethod={deleteTarget}
            onClose={() => {
              if (!isDeleting) {
                setDeleteTarget(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            isSubmitting={isDeleting}
          />
        </>
      ) : null}

      <PaymentMethodDetailsModal
        show={isDetailsOpen}
        onClose={() => {
          if (!isLoadingDetails) {
            setIsDetailsOpen(false);
          }
        }}
        details={selectedMethodDetails}
        isLoading={isLoadingDetails}
        errorMessage={detailsError}
      />
    </div>
  );
}
